/**
 * System Settings Store - Web Version
 * Browser-adapted implementation with full UI/privacy preferences
 */

import { createLogger } from '@ctrl-enter/tools/utils';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { PERMISSION_STATUS } from '../services/electron/constants';

const logger = createLogger('web:store:system-settings');

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type Theme = 'light' | 'dark' | 'system';

type PermissionStatus = 'granted' | 'denied' | 'not-determined' | 'unknown';

type CaptureOptions = {
  quality: 'low' | 'medium' | 'high';
  includeCursor: boolean;
  maxWidth: number;
  maxHeight: number;
  format: 'png' | 'jpeg';
  jpegQuality: number;
  thumbnailSize: { width: number; height: number };
};

type UIPreferences = {
  autoResize: boolean;
  showThumbnails: boolean;
  messageGrouping: boolean;
  soundEnabled: boolean;
  animationsEnabled: boolean;
  screenPreviewSize: 'small' | 'medium' | 'large';
  autoScreenshotOnOpen: boolean;
  showContextBar: boolean;
  autoScroll: boolean;
  /**
   * When true, Real-Time Mode searches ALL connected Enter accounts in
   * parallel and auto-switches the chat to whichever account owns the
   * open patient chart.
   *
   * Defaults to false — the safe option for demo environments where
   * an unexpected account switch could expose the wrong practice's data.
   * Users who work across multiple practices can enable this to have the
   * right account auto-selected whenever they open a ModMed chart.
   */
  rtmSearchAllAccounts: boolean;
  /**
   * Permanently dismisses the starter-prompts panel after the user has
   * sent their first message from a new/empty chat. Once set to true it
   * never reverts — new empty chats show a blank slate instead.
   */
  starterPromptsDismissed: boolean;
};

type PrivacySettings = {
  saveScreenshots: boolean;
  saveChatHistory: boolean;
  analyticsEnabled: boolean;
};

type UserPreferences = {
  theme: Theme;
  captureOptions: CaptureOptions;
  ui: UIPreferences;
  privacy: PrivacySettings;
};

type SystemSettingsState = {
  // User preferences (persisted)
  preferences: UserPreferences;
  defaultDisplayId: string; // Mock value for compatibility

  // Screen capture permissions (runtime state)
  screenCapturePermission: PermissionStatus;
  permissionLastChecked: number | undefined;

  // Runtime state
  isLoading: boolean;
  loadingMessage?: string;

  // Electron-specific properties (mocked for dev-menu compatibility)
  availableDisplays: never[]; // Always empty in web (no Electron display API)
  captureMode: false; // Always false in web (no Electron capture mode)

  // Error state
  error?: {
    message: string;
    details?: string;
    recoverable: boolean;
  };

  // Actions - Theme
  updateTheme: (theme: Theme) => void;

  // Actions - Capture Options
  updateCaptureOptions: (options: Partial<CaptureOptions>) => void;

  // Actions - UI Preferences
  updateUIPreferences: (ui: Partial<UIPreferences>) => void;

  // Actions - Privacy Settings
  updatePrivacySettings: (privacy: Partial<PrivacySettings>) => void;

  // Actions - General
  resetPreferences: () => void;

  // Actions - Screen Capture Permission
  setScreenCapturePermission: (permission: PermissionStatus) => void;
  resetScreenCapturePermission: () => void;

  // Actions - Loading and Error
  setLoading: (isLoading: boolean, message?: string) => void;
  setError: (error: { message: string; details?: string; recoverable: boolean }) => void;
  clearError: () => void;
};

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'system',
  captureOptions: {
    quality: 'high', // Higher quality for better vision analysis
    includeCursor: false, // Remove cursor to reduce complexity
    maxWidth: 1920, // Higher resolution for better detail recognition
    maxHeight: 1080,
    format: 'png', // PNG for lossless quality, will compress later if needed
    jpegQuality: 0.85, // High quality for vision analysis
    thumbnailSize: { width: 320, height: 240 },
  },
  ui: {
    autoResize: true,
    showThumbnails: true,
    messageGrouping: true,
    soundEnabled: true,
    animationsEnabled: true,
    screenPreviewSize: 'medium',
    autoScreenshotOnOpen: true, // Default to current behavior (auto-capture enabled)
    showContextBar: true,
    autoScroll: true,
    rtmSearchAllAccounts: false, // Off by default — safe for demo/single-practice accounts
    starterPromptsDismissed: false, // Show starter prompts until the user sends their first message
  },
  privacy: {
    saveScreenshots: false,
    saveChatHistory: true,
    analyticsEnabled: false,
  },
};

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useSystemSettingsStore = create<SystemSettingsState>()(
  persist(
    (set, get) => ({
      // Initial state
      preferences: DEFAULT_PREFERENCES,
      defaultDisplayId: 'primary', // Mock value for compatibility
      screenCapturePermission: PERMISSION_STATUS.GRANTED as PermissionStatus,
      permissionLastChecked: undefined,
      isLoading: false,

      // Mock Electron-specific state (for dev-menu compatibility)
      availableDisplays: [], // No display management in web
      captureMode: false, // No capture mode in web

      // ========================================================================
      // THEME ACTIONS
      // ========================================================================

      updateTheme: (theme: Theme) => {
        set(state => ({
          preferences: {
            ...state.preferences,
            theme,
          },
        }));

        // Sync with theme provider store if available
        if (typeof window !== 'undefined' && window.localStorage) {
          try {
            // Import theme provider dynamically to avoid circular dependencies
            import('../styles/theme-provider')
              .then(module => {
                // Use getState() method instead of the hook
                module.useThemeStore.getState().setTheme(theme);
              })
              .catch(error => {
                logger.warn('Could not sync theme with theme provider:', error);
              });
          } catch (error) {
            logger.warn('Could not sync theme with theme provider:', error);
          }
        }
      },

      // ========================================================================
      // CAPTURE OPTIONS ACTIONS
      // ========================================================================

      updateCaptureOptions: (options: Partial<CaptureOptions>) => {
        set(state => ({
          preferences: {
            ...state.preferences,
            captureOptions: {
              ...state.preferences.captureOptions,
              ...options,
            },
          },
        }));
      },

      // ========================================================================
      // UI PREFERENCES ACTIONS
      // ========================================================================

      updateUIPreferences: (ui: Partial<UIPreferences>) => {
        set(state => ({
          preferences: {
            ...state.preferences,
            ui: {
              ...state.preferences.ui,
              ...ui,
            },
          },
        }));
      },

      // ========================================================================
      // PRIVACY SETTINGS ACTIONS
      // ========================================================================

      updatePrivacySettings: (privacy: Partial<PrivacySettings>) => {
        set(state => ({
          preferences: {
            ...state.preferences,
            privacy: {
              ...state.preferences.privacy,
              ...privacy,
            },
          },
        }));
      },

      // ========================================================================
      // GENERAL ACTIONS
      // ========================================================================

      resetPreferences: () => {
        set({ preferences: DEFAULT_PREFERENCES });
      },

      // ========================================================================
      // SCREEN CAPTURE PERMISSION ACTIONS
      // ========================================================================

      setScreenCapturePermission: (permission: PermissionStatus) => {
        set({
          screenCapturePermission: permission,
          permissionLastChecked: Date.now(),
        });
      },

      resetScreenCapturePermission: () => {
        set({
          screenCapturePermission: PERMISSION_STATUS.GRANTED as PermissionStatus,
          permissionLastChecked: undefined,
        });
      },

      // ========================================================================
      // LOADING & ERROR ACTIONS
      // ========================================================================

      setLoading: (isLoading: boolean, message?: string) => {
        set({
          isLoading,
          ...(message !== undefined && { loadingMessage: message }),
        });
      },

      setError: (error: { message: string; details?: string; recoverable: boolean }) => {
        set({ error });
      },

      clearError: () => {
        const { error, ...rest } = get();
        void error; // Acknowledge we're removing this property
        set(rest);
      },
    }),
    {
      name: 'system-settings-store',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        // Only persist user preferences and defaultDisplayId
        preferences: state.preferences,
        defaultDisplayId: state.defaultDisplayId,
        // Don't persist: loading, error, permission state (runtime state)
      }),
      version: 9,
      // Migration function to handle schema changes
      migrate: (persistedState: unknown, version: number) => {
        // Handle migration from older versions
        if (!version || version < 1) {
          return {
            preferences: DEFAULT_PREFERENCES,
            defaultDisplayId: 'primary',
          };
        }

        if (version < 2) {
          // Migration from v1 to v2 - add any new preference fields
          const state = persistedState as Record<string, unknown>;
          return {
            ...state,
            preferences: {
              ...DEFAULT_PREFERENCES,
              ...(state.preferences as Record<string, unknown>),
            },
          };
        }

        if (version < 3) {
          // Migration from v2 to v3 - add defaultDisplayId
          const state = persistedState as Record<string, unknown>;
          return {
            ...state,
            defaultDisplayId: 'primary',
          };
        }

        if (version < 4) {
          // Migration from v3 to v4 - no changes needed for web
          return persistedState;
        }

        if (version < 5) {
          // Migration from v4 to v5 - add autoScreenshotOnOpen to ui preferences
          const state = persistedState as Record<string, unknown>;
          const preferences = state.preferences as Record<string, unknown>;
          const ui = preferences?.ui as Record<string, unknown>;
          return {
            ...state,
            preferences: {
              ...preferences,
              ui: {
                ...ui,
                autoScreenshotOnOpen: true,
              },
            },
          };
        }

        if (version < 6) {
          // Migration from v5 to v6 - add showContextBar to ui preferences
          const state = persistedState as Record<string, unknown>;
          const preferences = state.preferences as Record<string, unknown>;
          const ui = preferences?.ui as Record<string, unknown>;
          return {
            ...state,
            preferences: {
              ...preferences,
              ui: {
                ...ui,
                showContextBar: true,
              },
            },
          };
        }

        if (version < 7) {
          // Migration from v6 to v7 - add autoScroll to ui preferences (default on)
          const state = persistedState as Record<string, unknown>;
          const preferences = state.preferences as Record<string, unknown>;
          const ui = preferences?.ui as Record<string, unknown>;
          return {
            ...state,
            preferences: {
              ...preferences,
              ui: {
                ...ui,
                autoScroll: typeof ui?.autoScroll === 'boolean' ? ui.autoScroll : true,
              },
            },
          };
        }

        if (version < 8) {
          // Migration from v7 to v8 - add rtmSearchAllAccounts (default off)
          const state = persistedState as Record<string, unknown>;
          const preferences = state.preferences as Record<string, unknown>;
          const ui = preferences?.ui as Record<string, unknown>;
          return {
            ...state,
            preferences: {
              ...preferences,
              ui: {
                ...ui,
                rtmSearchAllAccounts: false,
              },
            },
          };
        }

        if (version < 9) {
          // Migration from v8 to v9 - add starterPromptsDismissed (default false)
          const state = persistedState as Record<string, unknown>;
          const preferences = state.preferences as Record<string, unknown>;
          const ui = preferences?.ui as Record<string, unknown>;
          return {
            ...state,
            preferences: {
              ...preferences,
              ui: {
                ...ui,
                starterPromptsDismissed: false,
              },
            },
          };
        }

        return persistedState;
      },
    }
  )
);

// ============================================================================
// SELECTOR HOOKS (ATOMIC SELECTORS)
// ============================================================================

// Theme selectors
export const useSystemTheme = () => useSystemSettingsStore(state => state.preferences.theme);
export const useUpdateTheme = () => useSystemSettingsStore(state => state.updateTheme);

// UI preferences selectors
export const useUIPreferences = () => useSystemSettingsStore(state => state.preferences.ui);
export const useUpdateUIPreferences = () =>
  useSystemSettingsStore(state => state.updateUIPreferences);
export const useShowContextBar = () =>
  useSystemSettingsStore(state => state.preferences.ui.showContextBar);
export const useAutoScroll = () =>
  useSystemSettingsStore(state => state.preferences.ui.autoScroll ?? true);
export const useRtmSearchAllAccounts = () =>
  useSystemSettingsStore(state => state.preferences.ui.rtmSearchAllAccounts ?? false);
export const useStarterPromptsDismissed = () =>
  useSystemSettingsStore(state => state.preferences.ui.starterPromptsDismissed ?? false);

// Privacy settings selectors
export const usePrivacySettings = () => useSystemSettingsStore(state => state.preferences.privacy);
export const useUpdatePrivacySettings = () =>
  useSystemSettingsStore(state => state.updatePrivacySettings);

// Capture options selectors
export const useCaptureOptions = () =>
  useSystemSettingsStore(state => state.preferences.captureOptions);
export const useUpdateCaptureOptions = () =>
  useSystemSettingsStore(state => state.updateCaptureOptions);

// General selectors
export const useResetPreferences = () => useSystemSettingsStore(state => state.resetPreferences);
export const useDefaultDisplayId = () => useSystemSettingsStore(state => state.defaultDisplayId);

// Loading/error selectors
export const useSystemSettingsLoading = () => useSystemSettingsStore(state => state.isLoading);
export const useSystemSettingsError = () => useSystemSettingsStore(state => state.error);
export const useSetLoading = () => useSystemSettingsStore(state => state.setLoading);
export const useSetError = () => useSystemSettingsStore(state => state.setError);
export const useClearError = () => useSystemSettingsStore(state => state.clearError);

// Screen capture permission selectors
export const useScreenCapturePermission = () =>
  useSystemSettingsStore(state => state.screenCapturePermission);
export const usePermissionLastChecked = () =>
  useSystemSettingsStore(state => state.permissionLastChecked);
export const useSetScreenCapturePermission = () =>
  useSystemSettingsStore(state => state.setScreenCapturePermission);
export const useResetScreenCapturePermission = () =>
  useSystemSettingsStore(state => state.resetScreenCapturePermission);
